// /src/pages/private/settings/account/AccountProfileThemeSection.jsx
// input-security: verified — fields: theme selection (id only, no free text)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BgColorsOutlined,
  CheckCircleFilled,
  LockFilled,
} from '@ant-design/icons';
import { useSecurity } from '../../../../SecurityContext';
import ThemeLivePreview from '../../admin/membersPerks/profileThemes/ThemeLivePreview';
import {
  fetchMyProfileThemeSettings,
  saveMyProfileTheme,
} from './accountProfileThemeApi';
import AccountProfileThemeSwatch from './AccountProfileThemeSwatch';

const RESOURCE_KEY = 'private.settings.account';
const ROUTE = '/api/user/settings/profile-theme';

const CATEGORY_LABELS = {
  member: 'Member',
  supporter: 'Supporter',
  patron: 'Patron',
  benefactor: 'Benefactor',
};

function sameThemeId(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

function themeToPreviewDraft(theme) {
  if (!theme) return null;
  return {
    effectKey: theme.effectKey || null,
    overlayEffectKeys: Array.isArray(theme.overlayEffectKeys)
      ? theme.overlayEffectKeys
      : [],
    effectConfig: theme.effectConfig || null,
    bannerMediaUrl: theme.bannerMediaUrl || null,
    bannerMediaType: theme.bannerMediaType || null,
    cardMediaUrl: theme.cardMediaUrl || null,
    cardMediaType: theme.cardMediaType || null,
  };
}

export default function AccountProfileThemeSection({
  onApplied = null,
  className = '',
}) {
  const { t } = useTranslation();
  const { isViewAsActive, viewAsUserId, viewAsLoading } = useSecurity();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [appliedThemeId, setAppliedThemeId] = useState(null);
  const [previewThemeId, setPreviewThemeId] = useState(null);
  const [themes, setThemes] = useState([]);
  const [userCategory, setUserCategory] = useState('member');

  const applyPayload = (payload = {}) => {
    const nextApplied =
      payload.selectedThemeId == null ? null : Number(payload.selectedThemeId);
    setAppliedThemeId(nextApplied);
    setPreviewThemeId(nextApplied);
    setThemes(Array.isArray(payload.themes) ? payload.themes : []);
    setUserCategory(payload.userCategory || 'member');
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchMyProfileThemeSettings();
      if (!data?.ok) throw new Error(data?.error || 'Load failed');
      applyPayload(data.data || {});
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewAsLoading) return undefined;
    load();
    return undefined;
  }, [load, viewAsUserId, viewAsLoading, isViewAsActive]);

  const handlePreview = (themeId) => {
    setPreviewThemeId(themeId == null ? null : Number(themeId));
    setError('');
  };

  const previewTheme = useMemo(() => {
    if (previewThemeId == null) return null;
    return themes.find((theme) => Number(theme.id) === Number(previewThemeId))
      || null;
  }, [themes, previewThemeId]);

  const previewLocked = Boolean(previewTheme?.locked);
  const canApply = !previewLocked
    && !sameThemeId(previewThemeId, appliedThemeId)
    && !saving
    && !loading;

  const handleApply = async () => {
    if (!canApply) return;
    if (previewTheme?.locked) {
      setError(t('accountProfileThemeLockedApply'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = await saveMyProfileTheme(previewThemeId);
      if (!data?.ok) throw new Error(data?.error || 'Save failed');
      applyPayload(data.data || {});
      if (typeof onApplied === 'function') {
        onApplied(data.data || {});
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const categoryLabel = CATEGORY_LABELS[userCategory] || CATEGORY_LABELS.member;

  const previewDraft = useMemo(
    () => themeToPreviewDraft(previewTheme),
    [previewTheme],
  );

  const rootClassName = [
    'account-card',
    'account-card--control',
    'account-card--profile-theme',
    className,
  ].filter(Boolean).join(' ');

  return (
    <section
      className={rootClassName}
      aria-labelledby="account-profile-theme-title"
      data-resource={RESOURCE_KEY}
      data-action="read"
      data-route={ROUTE}
      data-description="Account profile theme personalization"
    >
      <div className="account-card__lead">
        <div className="account-card__icon-wrap">
          <BgColorsOutlined className="account-card__icon" aria-hidden />
        </div>
        <div className="account-card__copy">
          <h4 id="account-profile-theme-title" className="account-card__title">
            {t('accountCardProfileThemeTitle')}
          </h4>
          <p className="account-card__subtitle">
            {t('accountCardProfileThemeDesc', { category: categoryLabel })}
          </p>
        </div>
      </div>

      {error ? <p className="account-profile-theme__error">{error}</p> : null}
      {loading || viewAsLoading ? (
        <p className="account-profile-theme__status">{t('profileThemeAdminLoading')}</p>
      ) : (
        <>
          <div
            className="account-profile-theme__grid"
            role="listbox"
            aria-label={t('accountCardProfileThemeTitle')}
          >
            <button
              type="button"
              role="option"
              aria-selected={previewThemeId == null}
              className={[
                'account-profile-theme__option',
                previewThemeId == null ? 'is-preview' : '',
                appliedThemeId == null ? 'is-applied' : '',
              ].filter(Boolean).join(' ')}
              disabled={saving}
              onClick={() => handlePreview(null)}
              data-resource={RESOURCE_KEY}
              data-action="read"
              data-route={ROUTE}
            >
              <AccountProfileThemeSwatch theme={null} />
              <span className="account-profile-theme__option-name">
                {t('profileThemeNone')}
              </span>
              <span className="account-profile-theme__option-meta">
                {t('profileThemeCategoryMember')}
              </span>
              {appliedThemeId == null ? (
                <span
                  className="account-profile-theme__badge account-profile-theme__badge--applied"
                  aria-label={t('accountProfileThemeApplied')}
                >
                  <CheckCircleFilled aria-hidden />
                </span>
              ) : null}
            </button>

            {themes.map((theme) => {
              const previewing = sameThemeId(previewThemeId, theme.id);
              const applied = sameThemeId(appliedThemeId, theme.id);
              const locked = Boolean(theme.locked);
              return (
                <button
                  key={theme.id}
                  type="button"
                  role="option"
                  aria-selected={previewing}
                  aria-disabled={locked ? true : undefined}
                  className={[
                    'account-profile-theme__option',
                    previewing ? 'is-preview' : '',
                    applied ? 'is-applied' : '',
                    locked ? 'is-locked' : '',
                  ].filter(Boolean).join(' ')}
                  disabled={saving}
                  onClick={() => handlePreview(theme.id)}
                  data-resource={RESOURCE_KEY}
                  data-action="read"
                  data-route={ROUTE}
                >
                  <AccountProfileThemeSwatch theme={theme} />
                  <span className="account-profile-theme__option-name">{theme.name}</span>
                  <span className="account-profile-theme__option-meta">
                    {CATEGORY_LABELS[theme.category] || CATEGORY_LABELS.member}
                  </span>
                  {applied ? (
                    <span
                      className="account-profile-theme__badge account-profile-theme__badge--applied"
                      aria-label={t('accountProfileThemeApplied')}
                    >
                      <CheckCircleFilled aria-hidden />
                    </span>
                  ) : null}
                  {locked ? (
                    <span
                      className="account-profile-theme__badge account-profile-theme__badge--locked"
                      aria-label={t('accountProfileThemeLocked')}
                      title={t('accountProfileThemeLocked')}
                    >
                      <LockFilled aria-hidden />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="account-profile-theme__actions">
            <button
              type="button"
              className="account-profile-theme__apply"
              disabled={!canApply}
              onClick={handleApply}
              data-resource={RESOURCE_KEY}
              data-action="write"
              data-route={ROUTE}
            >
              {saving
                ? t('profileThemeAdminSaving')
                : t('accountProfileThemeApply')}
            </button>
            {previewLocked ? (
              <p className="account-profile-theme__hint">
                {t('accountProfileThemeLockedHint')}
              </p>
            ) : sameThemeId(previewThemeId, appliedThemeId) ? (
              <p className="account-profile-theme__hint">
                {t('accountProfileThemePreviewHint')}
              </p>
            ) : (
              <p className="account-profile-theme__hint">
                {t('accountProfileThemeApplyHint')}
              </p>
            )}
          </div>

          <ThemeLivePreview
            draft={previewDraft}
            layout="row"
            showHeader={false}
            className="account-profile-theme__live-preview"
          />
        </>
      )}
    </section>
  );
}
